import { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Users, Target, TrendingUp, Settings2 } from 'lucide-react';
import TerritorySegmentConfig from './TerritorySegmentConfig';
import TamBreakdownCard from './TamBreakdownCard';
import WhitespaceSuggestions from './WhitespaceSuggestions';
import { APP_CHART, healthChartColour } from '../../lib/chartTheme';
import type { TerritoryAnalyticsSnapshot, TerritorySegmentConfig as SegmentConfigType, WhitespaceSuggestion, Account } from '../../types';

interface MemberSnapshot extends TerritoryAnalyticsSnapshot {
  user_name: string;
  user_email: string;
}

interface Props {
  mySnapshot: TerritoryAnalyticsSnapshot | null;
  teamSnapshots: MemberSnapshot[];
  teamAccounts: Account[];
  segmentConfig: SegmentConfigType | null;
  whitespaceSuggestions: WhitespaceSuggestion[];
  orgAvgHealth: number;
  orgId: string;
  userId: string;
  userEmail?: string;
  onSegmentSaved: () => void;
  onViewMember?: (userId: string, name: string) => void;
  onSuggestionsChanged: () => void;
}

const TIER_COLORS: Record<string, string> = { A: APP_CHART.brand, B: APP_CHART.focus, C: APP_CHART.accent };

function TeamScoreboard({ snapshots, orgAvg, onViewMember }: { snapshots: MemberSnapshot[]; orgAvg: number; onViewMember?: (userId: string, name: string) => void }) {
  const sorted = useMemo(() =>
    [...snapshots].sort((a, b) => b.territory_health_score - a.territory_health_score),
    [snapshots]
  );

  return (
    <div className="bg-app-surface rounded-lg border border-app-line overflow-hidden">
      <div className="px-6 py-4 border-b border-app-line flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-app-ink">Team Scoreboard</h3>
          <p className="text-xs text-app-muted mt-0.5">{snapshots.length} direct reports</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-app-muted">Org Avg Health</p>
          <p className="text-sm font-bold" style={{ color: healthChartColour(orgAvg) }}>{orgAvg.toFixed(0)}</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-app-canvas/60 text-[10px] font-semibold uppercase tracking-wider text-app-muted">
              <th className="px-4 py-2 text-left w-10">#</th>
              <th className="px-4 py-2 text-left">Rep</th>
              <th className="px-4 py-2 text-center">Health</th>
              <th className="px-4 py-2 text-right">Accounts</th>
              <th className="px-4 py-2 text-right">Avg Score</th>
              <th className="px-4 py-2 text-right">Contacts</th>
              <th className="px-4 py-2 text-right">Actions (30d)</th>
              <th className="px-4 py-2 text-right">Enrichment</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-app-line">
            {sorted.map((s, i) => (
              <tr
                key={s.user_id}
                onClick={() => onViewMember?.(s.user_id, s.user_name)}
                className={`hover:bg-forest-50/50 transition-colors ${onViewMember ? 'cursor-pointer' : ''}`}
              >
                <td className="px-4 py-2.5 text-xs text-app-muted font-mono">{i + 1}</td>
                <td className="px-4 py-2.5">
                  <p className="font-medium text-app-ink truncate max-w-[160px]">{s.user_name}</p>
                  <p className="text-[10px] text-app-muted truncate">{s.user_email}</p>
                </td>
                <td className="px-4 py-2.5 text-center">
                  <span className="inline-flex items-center justify-center w-10 h-6 rounded-full text-xs font-bold text-white" style={{ backgroundColor: healthChartColour(s.territory_health_score) }}>
                    {Math.round(s.territory_health_score)}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right text-xs text-app-ink">{s.accounts_owned}</td>
                <td className="px-4 py-2.5 text-right text-xs font-mono text-app-ink">{s.avg_tier_score?.toFixed(1) || '-'}</td>
                <td className="px-4 py-2.5 text-right text-xs text-app-ink">{s.total_contacts}</td>
                <td className="px-4 py-2.5 text-right text-xs text-app-ink">{s.total_actions_30d}</td>
                <td className="px-4 py-2.5 text-right text-xs text-app-muted">{s.enrichment_complete_pct?.toFixed(0)}%</td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-xs text-app-muted">
                  No direct reports found. Assign team members via the manager hierarchy.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function LeaderDashboard({ mySnapshot, teamSnapshots, teamAccounts, segmentConfig, whitespaceSuggestions, orgAvgHealth, orgId, userId, userEmail, onSegmentSaved, onViewMember, onSuggestionsChanged }: Props) {
  const [showSegmentConfig, setShowSegmentConfig] = useState(false);

  const teamTotals = useMemo(() => {
    const totals = { accounts: 0, contacts: 0, actions30d: 0, avgHealth: 0 };
    if (teamSnapshots.length === 0) return totals;
    for (const s of teamSnapshots) {
      totals.accounts += s.accounts_owned;
      totals.contacts += s.total_contacts;
      totals.actions30d += s.total_actions_30d;
      totals.avgHealth += s.territory_health_score;
    }
    totals.avgHealth = totals.avgHealth / teamSnapshots.length;
    return totals;
  }, [teamSnapshots]);

  const tierDistData = useMemo(() => {
    return teamSnapshots.map(s => ({
      name: s.user_name?.split(' ')[0] || 'Unknown',
      A: s.accounts_tiered_a,
      B: s.accounts_tiered_b,
      C: s.accounts_tiered_c,
    }));
  }, [teamSnapshots]);

  const industryData = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of teamSnapshots) {
      if (!s.industry_breakdown) continue;
      for (const [ind, cnt] of Object.entries(s.industry_breakdown)) {
        map.set(ind, (map.get(ind) || 0) + cnt);
      }
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, count]) => ({ name: name.length > 18 ? name.slice(0, 18) + '...' : name, count }));
  }, [teamSnapshots]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-app-surface rounded-lg border border-app-line p-5">
          <div className="w-9 h-9 bg-app-canvas rounded-lg flex items-center justify-center mb-3">
            <Users className="w-4 h-4 text-app-muted" />
          </div>
          <p className="text-2xl font-bold text-app-ink">{teamSnapshots.length}</p>
          <p className="text-xs text-app-muted">Team Size</p>
        </div>
        <div className="bg-app-surface rounded-lg border border-app-line p-5">
          <div className="w-9 h-9 bg-app-canvas rounded-lg flex items-center justify-center mb-3">
            <Target className="w-4 h-4 text-app-muted" />
          </div>
          <p className="text-2xl font-bold text-app-ink">{teamTotals.accounts}</p>
          <p className="text-xs text-app-muted">Team Accounts</p>
        </div>
        <div className="bg-app-surface rounded-lg border border-app-line p-5">
          <p className="text-2xl font-bold" style={{ color: healthChartColour(teamTotals.avgHealth) }}>{teamTotals.avgHealth.toFixed(0)}</p>
          <p className="text-xs text-app-muted">Team Avg Health</p>
          <p className="text-[10px] text-app-muted mt-0.5">Org avg: {orgAvgHealth.toFixed(0)}</p>
        </div>
        <div className="bg-app-surface rounded-lg border border-app-line p-5">
          <div className="w-9 h-9 bg-app-canvas rounded-lg flex items-center justify-center mb-3">
            <TrendingUp className="w-4 h-4 text-app-muted" />
          </div>
          <p className="text-2xl font-bold text-app-ink">{teamTotals.actions30d}</p>
          <p className="text-xs text-app-muted">Team Actions (30d)</p>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => setShowSegmentConfig(!showSegmentConfig)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-app-muted bg-app-surface border border-app-line rounded-lg hover:bg-app-canvas transition-colors"
        >
          <Settings2 className="w-4 h-4" />
          {showSegmentConfig ? 'Hide Segment Config' : 'Configure Territory Segment'}
        </button>
      </div>

      {showSegmentConfig && (
        <TerritorySegmentConfig
          config={segmentConfig}
          orgId={orgId}
          userId={userId}
          onSaved={() => { setShowSegmentConfig(false); onSegmentSaved(); }}
        />
      )}

      <TeamScoreboard snapshots={teamSnapshots} orgAvg={orgAvgHealth} onViewMember={onViewMember} />

      <TamBreakdownCard accounts={teamAccounts} title="Team TAM Breakdown" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {tierDistData.length > 0 && (
          <div className="bg-app-surface rounded-lg border border-app-line p-6">
            <h3 className="text-sm font-semibold text-app-ink mb-4">Account Distribution by Rep</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={tierDistData}>
                <CartesianGrid strokeDasharray="3 3" stroke={APP_CHART.line} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: APP_CHART.muted }} />
                <YAxis tick={{ fontSize: 10, fill: APP_CHART.muted }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="A" stackId="tier" fill={TIER_COLORS.A} />
                <Bar dataKey="B" stackId="tier" fill={TIER_COLORS.B} />
                <Bar dataKey="C" stackId="tier" fill={TIER_COLORS.C} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {industryData.length > 0 && (
          <div className="bg-app-surface rounded-lg border border-app-line p-6">
            <h3 className="text-sm font-semibold text-app-ink mb-4">Team Industry Coverage</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={industryData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={APP_CHART.line} />
                <XAxis type="number" tick={{ fontSize: 10, fill: APP_CHART.muted }} />
                <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 10, fill: APP_CHART.muted }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="count" fill={APP_CHART.brand} radius={[0, 4, 4, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <WhitespaceSuggestions
        orgId={orgId}
        userId={userId}
        suggestions={whitespaceSuggestions}
        teamMembers={[
          { user_id: userId, user_name: 'Me', user_email: userEmail || '' },
          ...teamSnapshots.map(s => ({
            user_id: s.user_id,
            user_name: s.user_name,
            user_email: s.user_email,
          })),
        ]}
        onSuggestionsChanged={onSuggestionsChanged}
      />
    </div>
  );
}
